import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";

import { Input } from "../../components/Input";
import { useDarkMode } from "../../context/DarkModeContext";
import { Button } from "../../components/Button";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { changeEmail } from "../../features/store/authSlice";
import { MessageIcon } from "../../assets/icons";

//Validation Schema
const schema = yup.object({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
});

const EmailAddress = () => {
  const { darkMode } = useDarkMode();
  const dispatch = useAppDispatch();
  const { user, loading } = useAppSelector((state) => state.auth); //Get user from Redux state

  const isGoogleLinked = Boolean(user?.google_id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: user?.email || "", //Prefill email from user state
      password: "",
    },
  });

  //Handle Form Submission
  const onSubmit = async (data: { email: string; password: string }) => {
    if (isGoogleLinked) {
      toast.warning(
        "Cannot change email/password when Google account is linked"
      );
      return;
    }
    try {
      await dispatch(changeEmail(data)).unwrap();
      reset({ password: "" });
      toast.success("Email updated successfully!");
    } catch (error) {
      toast.error("Failed to update email. Please try again.");
    }
  };

  return (
    <div className="mx-2 w-[300px] md:w-[578px] p-6 font-satoshi bg-white dark:bg-black border border-[#EAECF0] dark:border-[#667085] rounded-lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm md:text-xl font-medium dark:text-white mb-4">
          Email Address
        </p>

        {/*Email Input (Prefilled) */}
        <div>
          <Input
            type="email"
            placeholder="Email address"
            register={register}
            name="email"
            required
            icon={<MessageIcon theme={darkMode ? "dark" : "light"} />}
            disabled={isGoogleLinked}
            className={`w-full dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] ${
              isGoogleLinked ? "opacity-60 cursor-not-allowed" : ""
            }`}
          />
          {errors.email?.message && (
            <p className="text-red-500 text-xs">
              {String(errors.email.message)}
            </p>
          )}
        </div>

        {/*Password Input */}
        <div>
          <Input
            type="password"
            placeholder="Password"
            register={register}
            name="password"
            required
            isPassword
            disabled={isGoogleLinked}
            className={`w-full dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] ${
              isGoogleLinked ? "opacity-60 cursor-not-allowed" : ""
            }`}
          />
          {errors.password?.message && (
            <p className="text-red-500 text-xs">
              {String(errors.password.message)}
            </p>
          )}
        </div>

        {/*Save Changes Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            bgColor="bg-white dark:bg-[#FFFFFF1A]"
            disabled={loading || isGoogleLinked}
            className={`w-[142px] border border-[#EAECF0] dark:border-[#475467] dark:text-white mt-4 py-2 rounded-lg text-sm ${
              loading || isGoogleLinked? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmailAddress;
